import React, { useState } from 'react';
import {
    Alert,
    AlertIcon,
    Box,
    Flex,
    Heading,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Spinner,
    Text,
    useToast,
} from '@chakra-ui/react';
import { useMutation, useQueryClient } from 'react-query';
import { HistoriqueBDList } from './HistoriqueBDList';
import { useHistoriqueBD } from '../../hooks/useHistoriqueBD';
import { deleteMission, Mission, updateMission } from '../mission/MissionService';
import { Candidature, getCandidaturesByMissionId } from '../Candidature/CandidatureService';
import { useAuth } from '../../features/auth/hooks/useAuth';

// Utilitaire pour formater une date depuis un tableau de nombres
const formatDate = (dateArray: number[]): string => {
    if (!dateArray || dateArray.length < 3) return 'Date non disponible';
    const [year, month, day, hour = 0, minute = 0] = dateArray;
    const timestamp = Date.UTC(year, month - 1, day, hour, minute);
    const date = new Date(timestamp);
    return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const HistoriqueBDPage: React.FC = () => {
    const { getFullName } = useAuth();
    const toast = useToast();
    const queryClient = useQueryClient();
    const accentColor = "#420073";

    // Récupération des missions
    const { missions, isLoading, isError, error } = useHistoriqueBD();

    // États pour la modal
    const [selectedMission, setSelectedMission] = useState<Mission | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [candidatures, setCandidatures] = useState<Candidature[]>([]);
    const [loadingCandidatures, setLoadingCandidatures] = useState(false);

    // Mutation suppression
    const deleteMutation = useMutation((m: Mission) => deleteMission(m.id), {
        onSuccess: () => {
            toast({ title: 'Mission supprimée', status: 'success' });
            queryClient.invalidateQueries('missions');
        },
        onError: () => {
            toast({ title: 'Erreur lors de la suppression', status: 'error' });
        },
    });

    // Mutation mise à jour (exemple de titre + description)
    const updateMutation = useMutation(
        (m: Mission) => updateMission(m.id, {
            opportunite: m.opportunite,
            description: m.description,
            status: m.status,
            businessDeveloperSub: m.businessDeveloperSub,
        }),
        {
            onSuccess: () => {
                toast({ title: 'Mission mise à jour', status: 'success' });
                queryClient.invalidateQueries('missions');
            },
            onError: () => {
                toast({ title: 'Erreur lors de la mise à jour', status: 'error' });
            },
        }
    );

    // Ouvre la modal et charge les candidatures
    const openModal = async (mission: Mission) => {
        setSelectedMission(mission);
        setIsModalOpen(true);
        setLoadingCandidatures(true);
        try {
            const data = await getCandidaturesByMissionId(mission.id);
            setCandidatures(data);
        } catch {
            toast({
                title: 'Erreur',
                description: 'Impossible de charger les candidatures',
                status: 'error'
            });
            setCandidatures([]);
        } finally {
            setLoadingCandidatures(false);
        }
    };

    // Édition simplifiée via prompt
    const handleEdit = (mission: Mission) => {
        const newTitre = prompt('Nouveau titre', mission.opportunite);
        const newDesc = prompt('Nouvelle description', mission.description);
        if (newTitre !== null && newDesc !== null) {
            updateMutation.mutate({ ...mission, opportunite: newTitre, description: newDesc });
        }
    };

    if (isLoading) {
        return (
            <Flex justify="center" align="center" minH="200px">
                <Spinner size="xl" color={accentColor} />
            </Flex>
        );
    }

    if (isError) {
        return (
            <Alert status="error">
                <AlertIcon />
                {error?.message || 'Erreur lors du chargement des missions'}
            </Alert>
        );
    }

    return (
        <Box p={4} maxW="container.xl" mx="auto">
            <Heading mb={8} color={accentColor}>Mes Missions</Heading>

            <HistoriqueBDList
                missions={missions}
                onOpenModal={openModal}
                onDelete={deleteMutation.mutate}
                onEdit={handleEdit}
            />

            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size="xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader color={accentColor}>
                        Candidatures pour : {selectedMission?.opportunite}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        {loadingCandidatures ? (
                            <Flex justify="center" py={10}>
                                <Spinner size="xl" color={accentColor} />
                            </Flex>
                        ) : candidatures.length > 0 ? (
                            candidatures.map((c) => (
                                <Box key={c.id} p={4} mb={4} borderWidth="1px" borderRadius="md">
                                    <Text fontWeight="bold">Nom : {c.nom}</Text>
                                    <Text>Date : {formatDate(c.publicationDate)}</Text>
                                </Box>
                            ))
                        ) : (
                            <Text textAlign="center" py={4}>
                                Aucune candidature pour cette mission
                            </Text>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default HistoriqueBDPage;
